import React from "react";

import classNames from "classnames";
import PropTypes from "prop-types";

import i18n, { LANGUAGES2, changeLocale } from "components/i18n/source";
import { useAppContext } from "components/store/source";
import { Button, Dropdown, Image, Modal, Text } from "components/ui/source";

import { IconCheckmark, IconRightChevron } from "icons";

import styles from "./Selector.module.scss";

function LanguageLabel({ label, flag }) {
  // flags come from https://flagicons.lipis.dev/

  const imageUrl = flag =>
    `https://images.sonder.com/image/upload/v1752769860/sondercom/flags/${flag}.svg`;

  return (
    <>
      <Image
        className={styles.languageFlag}
        src={imageUrl(flag)}
        alt={`${flag} flag`}
      />
      <Text types={["body1"]}>{label}</Text>
    </>
  );
}

LanguageLabel.propTypes = {
  label: PropTypes.string,
  flag: PropTypes.string,
};

function LanguageButton({ value, selected, ...labelProps }) {
  const handleChange = locale => () => changeLocale(locale);

  return (
    <Button
      className={classNames(styles.itemButton, selected && styles.selected)}
      type="navbar_signup"
      onClick={handleChange(value)}
      fullWidth
      centered={false}
    >
      {selected && (
        <IconCheckmark className={styles.checkmark} size="md" color="black" />
      )}
      <LanguageLabel {...labelProps} />
    </Button>
  );
}

LanguageButton.propTypes = {
  value: PropTypes.string,
  selected: PropTypes.bool,
  ...LanguageLabel.propTypes,
};

function LanguageList({ selectedLocale }) {
  return (
    <div className={styles.listWrapper}>
      <Text
        className={styles.title}
        types={["body1", "inline"]}
        translateKey="navbar.languageSelector.title"
      />
      <ul className={styles.list}>
        {LANGUAGES2.map(lang => (
          <li className={styles.item} key={lang.value}>
            <LanguageButton
              selected={selectedLocale === lang.value}
              {...lang}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

LanguageList.propTypes = {
  selectedLocale: PropTypes.string,
};
function LanguageSelector({ className, isMobile }) {
  const {
    languageSelectorPopupIsOpen,
    closeLanguageSelectorPopup,
    openLanguageSelectorPopup,
  } = useAppContext();

  const selectedLocale =
    LANGUAGES2.find(lang => lang.value === i18n.language) ||
    // To handle the edge case when an user opens s.com with legacy locale (fr, es, it)
    LANGUAGES2.find(lang => lang.value.includes(i18n.language));

  const modalProps = {
    isOpen: languageSelectorPopupIsOpen,
    onRequestClose: closeLanguageSelectorPopup,
    className: styles.modalContent,
  };

  if (isMobile) {
    return (
      <>
        <Modal modalProps={modalProps}>
          <Modal.Close
            className={styles.modalCloseButton}
            onClick={closeLanguageSelectorPopup}
          />
          <LanguageList selectedLocale={selectedLocale.value} />
        </Modal>

        <Button
          className={className}
          type="navbar_signup"
          onClick={openLanguageSelectorPopup}
          upperCase={false}
        >
          <LanguageLabel {...selectedLocale} />
          <IconRightChevron
            className={styles.rightChevron}
            size="sm"
            color="black"
          />
        </Button>
      </>
    );
  }

  return (
    <Dropdown
      className={classNames(styles.selectorDropdown, className)}
      alignRight
      width="fit-content"
      buttonType="navbar_signup"
      label={<LanguageLabel {...selectedLocale} />}
      outlineOnFocus={false}
      noIcon
    >
      <LanguageList selectedLocale={selectedLocale.value} />
    </Dropdown>
  );
}

LanguageSelector.propTypes = {
  className: PropTypes.string,
  isMobile: PropTypes.bool,
};

export default LanguageSelector;
